import { DrawerDialogComponent } from '@theme/drawer-dialog';
import { CartAddEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/** Mobile breakpoint — matches the theme's --breakpoint-sm (750px). */
const mobileQuery = window.matchMedia('(max-width: 749px)');

/** Minimum downward distance (px) to trigger dismiss on release. */
const DRAG_DISMISS_DISTANCE = 80;

/** Minimum downward velocity (px/ms) to trigger dismiss on release regardless of distance. */
const DRAG_DISMISS_VELOCITY = 0.4;

/** Minimum vertical movement (px) required to commit to a drag from the body
 *  (prevents accidental hijacking of taps / horizontal scrolls). */
const DRAG_COMMIT_THRESHOLD = 8;

/** Minimum dy dominance over dx (px) to interpret a gesture as a vertical drag. */
const DRAG_AXIS_DOMINANCE = 6;

/**
 * Mobile-only bottom-sheet mini-cart shown after a quick-add.
 *
 * Extends DrawerDialogComponent (same as cart / menu drawers) so open/close,
 * scroll-lock, Escape, and outside-click are all inherited.
 *
 * Drag-to-dismiss: touching the cart-drawer__header and dragging down closes
 * the sheet. A small elastic upward drag (≤15px) is allowed for feel.
 *
 * @extends {DrawerDialogComponent}
 */
/** Auto-hide delay for the "added to your cart" banner (ms). */
const BANNER_AUTO_HIDE_MS = 1500;

class QuickCartSheet extends DrawerDialogComponent {
  // ─── Cart-add state ───────────────────────────────────────────────────────────
  /** @type {string | null} */
  #pendingVariantId = null;
  /** @type {number | null} */
  #bannerHideTimer = null;

  // ─── Drag state ───────────────────────────────────────────────────────────────
  #dragStartX = 0;
  #dragStartY = 0;
  #dragLastY = 0;
  #dragLastTime = 0;
  #isDragging = false;
  /** True once vertical intent has been confirmed and the sheet is following the finger. */
  #dragCommitted = false;
  /** True when the gesture started on the header handle (always drag, never scroll). */
  #dragFromHeader = false;

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartEvent, { capture: true });
    // Listen on `this` (not refs.dialog) so the listener survives morphSection replacing
    // the <dialog> element — morphdom may detach the old node, losing any listeners on it.
    this.addEventListener('touchstart', this.#onDragStart, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartEvent, { capture: true });
    this.removeEventListener('touchstart', this.#onDragStart);
  }

  // ─── Cart add ────────────────────────────────────────────────────────────────

  #handleCartEvent = (/** @type {CartAddEvent | CartUpdateEvent} */ event) => {
    // While the sheet is open on mobile, suppress cart-drawer for ALL cart:update
    // events (quantity changes, removals, etc. all fire the same event name).
    if (this.refs.dialog.open && mobileQuery.matches) {
      if (event.detail?.data) event.detail.data.suppressCartDrawer = true;
      if (event.detail.data?.didError) return;

      const isProductAdd =
        event.detail.data?.source === 'product-form-component' && this.#isQuickAddSource(event.target);

      // Close the sheet automatically if the cart is emptied
      if (event.detail.data?.itemCount === 0) {
        this.#hideAddedBanner();
        this.closeDialog();
        return;
      }

      if (isProductAdd) {
        this.#pendingVariantId = event.detail.sourceId ?? null;
        this.#refreshContent(event.detail.data?.sections).then(() => this.#showAddedBanner());
        return;
      }

      this.#hideAddedBanner();
      this.#refreshContent(event.detail.data?.sections);
      return;
    }

    // New quick-add on mobile: open the sheet.
    if (event.detail.data?.didError) return;
    if (!mobileQuery.matches) return;
    if (!this.#isQuickAddSource(event.target)) return;

    // Stamp synchronously in capture phase — cart-drawer reads this in bubble phase.
    event.detail.data.suppressCartDrawer = true;
    this.#pendingVariantId = event.detail.sourceId ?? null;

    this.#processCartAdd(event.detail.data?.sections);
  };

  async #processCartAdd(sections) {
    // Wait for the quick-add dialog to finish closing before calling showDialog(),
    // otherwise showDialog() reads window.scrollY = 0 (body is still position:fixed
    // from the quick-add modal's scroll-lock) and corrupts the saved scroll offset.
    const quickAddEl = document.getElementById('quick-add-dialog');
    if (quickAddEl instanceof HTMLElement && quickAddEl.querySelector('dialog[open]')) {
      await new Promise((resolve) => {
        quickAddEl.addEventListener(DialogCloseEvent.eventName, resolve, { once: true });
        setTimeout(resolve, 600); // safety fallback
      });
    }

    if (!this.refs.dialog.open) this.open();
    await this.#refreshContent(sections);
    this.#showAddedBanner();
  }

  async #refreshContent(sections) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;
    try {
      if (sections?.[sectionId]) {
        await morphSection(sectionId, sections[sectionId]);
      } else {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      }
    } catch {
      // Stale content beats an error on a checkout path.
    }
  }

  #showAddedBanner() {
    const banner = this.querySelector('[data-added-banner]');
    if (!banner) {
      this.#hideAddedBanner();
      return;
    }

    if (this.#bannerHideTimer != null) {
      clearTimeout(this.#bannerHideTimer);
      this.#bannerHideTimer = null;
    }

    if (!this.#pendingVariantId) {
      this.#hideAddedBanner();
      return;
    }

    const row = this.querySelector(`[data-variant-id="${this.#pendingVariantId}"]`);
    if (!row) {
      this.#hideAddedBanner();
      return;
    }

    const title = row instanceof HTMLElement ? (row.dataset.variantTitle ?? '') : '';
    const textEl = banner.querySelector('[data-added-banner-text]');
    const message =
      title && title !== 'Default Title' ? `${title} — added to your cart` : 'Added to your cart';
    if (textEl) textEl.textContent = message;
    else banner.textContent = message;
    banner.removeAttribute('hidden');

    this.#bannerHideTimer = window.setTimeout(() => {
      this.#hideAddedBanner();
    }, BANNER_AUTO_HIDE_MS);
  }

  #hideAddedBanner() {
    if (this.#bannerHideTimer != null) {
      clearTimeout(this.#bannerHideTimer);
      this.#bannerHideTimer = null;
    }

    this.#pendingVariantId = null;

    const banner = this.querySelector('[data-added-banner]');
    banner?.setAttribute('hidden', '');
  }

  // ─── Drag-to-dismiss ─────────────────────────────────────────────────────────

  #onDragStart = (/** @type {TouchEvent} */ event) => {
    if (!this.refs.dialog.open) return;
    if (!(event.target instanceof Element)) return;

    const fromHeader = Boolean(event.target.closest('.cart-drawer__header'));
    // Outside the header, the sheet only becomes draggable when the scroll
    // container is at the top — otherwise the list should scroll normally.
    if (!fromHeader && !this.#isAtScrollTop(event.target)) return;

    const touch = event.touches[0];
    this.#dragStartX = touch.clientX;
    this.#dragStartY = touch.clientY;
    this.#dragLastY = touch.clientY;
    this.#dragLastTime = Date.now();
    this.#isDragging = true;
    // Header drags are committed immediately (1:1 follow). Body drags defer
    // until we see dominant downward intent.
    this.#dragCommitted = fromHeader;
    this.#dragFromHeader = fromHeader;

    if (fromHeader) {
      // Disable transitions for direct 1:1 drag feel.
      this.refs.dialog.style.transition = 'none';
    }

    window.addEventListener('touchmove', this.#onDragMove, { passive: false });
    window.addEventListener('touchend', this.#onDragEnd, { once: true });
    window.addEventListener('touchcancel', this.#onDragCancel, { once: true });
  };

  #onDragMove = (/** @type {TouchEvent} */ event) => {
    if (!this.#isDragging) return;

    const touch = event.touches[0];
    const y = touch.clientY;
    const dy = y - this.#dragStartY;
    const dx = touch.clientX - this.#dragStartX;

    // Before committing, decide whether this gesture is a sheet drag or
    // should be released back to the browser (horizontal pan / small tap).
    if (!this.#dragCommitted) {
      // Horizontal intent wins — abandon drag so the browser can do its thing.
      if (Math.abs(dx) > Math.abs(dy) + DRAG_AXIS_DOMINANCE) {
        this.#cleanupDragListeners();
        return;
      }
      // Too small to be a gesture yet — wait.
      if (Math.abs(dy) < DRAG_COMMIT_THRESHOLD) return;
      // Body drags only engage on a downward swipe (upward shouldn't close).
      if (!this.#dragFromHeader && dy < 0) {
        this.#cleanupDragListeners();
        return;
      }
      this.#dragCommitted = true;
      this.refs.dialog.style.transition = 'none';
    }

    this.#dragLastY = y;
    this.#dragLastTime = Date.now();

    if (dy >= 0) {
      // Downward: 1:1 follow.
      this.refs.dialog.style.transform = `translateY(${dy}px)`;
    } else {
      // Upward: elastic resistance — max 15px for aesthetic feel.
      this.refs.dialog.style.transform = `translateY(${Math.max(dy * 0.15, -15)}px)`;
    }

    // Prevent browser scroll/swipe-back interference during drag.
    event.preventDefault();
  };

  /**
   * True when the nearest scrollable ancestor of `target` within the sheet is
   * scrolled to the top (or there is no such ancestor). Mirrors iOS
   * bottom-sheet behavior: scroll the list first, drag the sheet second.
   * @param {Element} target
   */
  #isAtScrollTop(target) {
    let el = target;
    while (el && el !== this) {
      if (el instanceof HTMLElement) {
        const canScroll = el.scrollHeight > el.clientHeight;
        const style = canScroll ? getComputedStyle(el) : null;
        if (canScroll && style && /(auto|scroll)/.test(style.overflowY)) {
          return el.scrollTop <= 0;
        }
      }
      el = el.parentElement;
    }
    return true;
  }

  #onDragEnd = (/** @type {TouchEvent} */ event) => {
    if (!this.#isDragging) return;
    this.#cleanupDragListeners();

    const endY = event.changedTouches[0]?.clientY ?? this.#dragStartY;
    const dy = endY - this.#dragStartY;

    // Velocity: px/ms over the last ≤250ms of movement.
    const elapsed = Date.now() - this.#dragLastTime;
    const velocity =
      elapsed > 0 && elapsed < 250 ? (endY - this.#dragLastY) / elapsed : 0;

    if (dy > DRAG_DISMISS_DISTANCE || velocity > DRAG_DISMISS_VELOCITY) {
      this.#dismissByDrag();
    } else {
      this.#snapBack();
    }
  };

  #onDragCancel = () => {
    if (!this.#isDragging) return;
    this.#cleanupDragListeners();
    this.#snapBack();
  };

  #cleanupDragListeners() {
    this.#isDragging = false;
    window.removeEventListener('touchmove', this.#onDragMove);
    window.removeEventListener('touchend', this.#onDragEnd);
    window.removeEventListener('touchcancel', this.#onDragCancel);
  }

  /** Snap the dialog back to its resting position with spring easing. */
  #snapBack() {
    const dialog = this.refs.dialog;
    dialog.style.transition = `transform 0.4s var(--animation-easing, cubic-bezier(0.16, 1, 0.3, 1))`;
    dialog.style.transform = '';
    dialog.addEventListener('transitionend', () => {
      dialog.style.transition = '';
    }, { once: true });
  }

  /**
   * Continue the drag momentum off-screen, then close without replaying the
   * normal exit animation (the dialog is already visually gone).
   *
   * Setting `animation: none` before calling closeDialog() causes onAnimationEnd
   * (which uses getAnimations()) to resolve immediately with an empty array,
   * so closeDialogInternal skips straight to the body cleanup and dialog.close().
   *
   * We intentionally keep the off-screen `transform` set during closeDialog() so
   * the dialog never snaps back into view for a frame before dialog.close() hides it.
   */
  async #dismissByDrag() {
    const dialog = this.refs.dialog;

    // Continue the slide off-screen from wherever the finger released.
    dialog.style.transition = 'transform 0.22s ease-in';
    dialog.style.transform = 'translateY(110vh)';

    await new Promise((resolve) => {
      dialog.addEventListener('transitionend', resolve, { once: true });
      setTimeout(resolve, 350); // fallback in case transitionend doesn't fire
    });

    // Keep transform off-screen during close so there is no snap-back flash.
    // animation: none causes getAnimations() to return [] so closeDialogInternal
    // skips the exit animation and calls dialog.close() immediately.
    dialog.style.transition = 'none';
    dialog.style.animation = 'none';
    await this.closeDialog();

    // Safe to clear now — dialog is display:none from dialog.close().
    dialog.style.transform = '';
    dialog.style.transition = '';
    dialog.style.animation = '';
  }

  // ─── Utilities ───────────────────────────────────────────────────────────────

  #isQuickAddSource(target) {
    if (!(target instanceof Element)) return false;
    if (target.closest('cart-drawer-component')) return false;
    return Boolean(
      target.closest('quick-add-component') || target.closest('#quick-add-dialog')
    );
  }
}

customElements.define('quick-cart-sheet', QuickCartSheet);
